/**
 * Which level of detail the Atlas map draws, decided by the zoom.
 *
 * Far away the map draws provinces, which is one small cached topology for the whole
 * country. Once only a handful of them are left on screen, their census sections are worth
 * loading — all of them, so the whole visible rectangle ends up with data instead of a
 * single province in the middle of the frame. Zooming back out returns to provinces.
 *
 * The decision is a count, not a ratio, for two reasons: it is what guarantees the frame is
 * filled, and it brings the switch to a zoom the user actually reaches. Three knobs keep it
 * quiet:
 *
 * - `enter` / `exit`: how many provinces in the frame still mean sections. Leaving needs
 *   more of them than entering (hysteresis), so the level does not flicker at the boundary.
 * - `preload`: one step earlier, the sections of the frame and its neighbours are fetched,
 *   so the switch itself does not have to wait for the network.
 *
 * Nothing here touches the DOM or the map library: the boxes come from the geometry the map
 * already holds, which keeps the rule testable and the tuning measurable.
 */

/** Geographic extent as [west, south, east, north], in degrees. */
export type BoundingBox = [number, number, number, number]

export type DetailLevel = 'provinces' | 'sections'

/** A province with the box of its geometry, built once from the provinces topology. */
export type ProvinceBox = { id: number; box: BoundingBox }

/** Province counts: entering the section level, leaving it, and preloading it. */
export type DetailThresholds = { enter: number; exit: number; preload: number }

/**
 * The entry limit is set with the user's own zoom in hand — it is the point where the switch
 * still came too late — and the other two keep one step of margin around it.
 */
export const DEFAULT_THRESHOLDS: DetailThresholds = { enter: 9, exit: 11, preload: 10 }

/** How far past the frame the preload looks, as a share of the frame size. */
export const PRELOAD_MARGIN = 0.35

const overlap = (a: BoundingBox, b: BoundingBox): number => {
  const width = Math.min(a[2], b[2]) - Math.max(a[0], b[0])
  const height = Math.min(a[3], b[3]) - Math.max(a[1], b[1])

  return width > 0 && height > 0 ? width * height : 0
}

/** The view grown by a share of its own size, to look just past the edge of the screen. */
const expanded = (view: BoundingBox, margin: number): BoundingBox => {
  const dx = (view[2] - view[0]) * margin
  const dy = (view[3] - view[1]) * margin

  return [view[0] - dx, view[1] - dy, view[2] + dx, view[3] + dy]
}

/**
 * The provinces touching the view, lowest id first: all of them get drawn at the section
 * level, which is what fills the frame. A province that only touches the edge (no area in
 * common) does not count; with a `margin`, the neighbours just outside the frame do.
 */
export const provincesInView = (
  view: BoundingBox,
  provinces: ProvinceBox[],
  margin = 0
): ProvinceBox[] => {
  const sight = margin > 0 ? expanded(view, margin) : view

  return provinces
    .filter(province => overlap(province.box, sight) > 0)
    .sort((a, b) => a.id - b.id)
}

/**
 * The level to draw for a view, given the level already on screen. Leaving the section
 * level allows more provinces than entering it, which is what keeps the boundary quiet.
 */
export const decideLevel = (
  view: BoundingBox,
  provinces: ProvinceBox[],
  current: DetailLevel,
  thresholds: DetailThresholds = DEFAULT_THRESHOLDS
): DetailLevel => {
  const count = provincesInView(view, provinces).length
  const limit = current === 'sections' ? thresholds.exit : thresholds.enter

  return count > 0 && count <= limit ? 'sections' : 'provinces'
}

/**
 * Whether the sections are worth fetching now: the frame and its neighbours are few enough
 * that the switch is close, so the request runs while provinces are still on screen.
 */
export const shouldPreload = (
  view: BoundingBox,
  provinces: ProvinceBox[],
  thresholds: DetailThresholds = DEFAULT_THRESHOLDS
): boolean => provincesInView(view, provinces, PRELOAD_MARGIN).length <= thresholds.preload
